/* eslint-disable react-hooks/exhaustive-deps */
import React from 'react';
import { create } from 'zustand';

import { DisciplineSub, DisciplineSubGrouping, PaperType } from '@blocks/types/dictionary';
import { Settings } from '@blocks/types/settings';
import { getDevice, getPageType } from '@blocks/utils/gtm/gtm-key-getters';

import { getDisciplines, getOffset, getPapers, getSettings } from '../api';
import { isBrowser } from '../utils';

type Offset = {
  client_utc_raw_offset_min: number;
  client_utc_offset_min: number;
  client_utc_offset_o: string;
  client_utc_offset_p: string;
  client_datetime: string;
  utc_datetime: string;
};

type SettingsStore = {
  isLoading: boolean;
  isAssignment: boolean;
  settings?: Settings;
  fetchSettings: (domain: string) => void;
} & Partial<Offset>;

function pushGtmObjects(objects: Settings['gtm_datalayer_objects'] = []) {
  if (objects.length && Array.isArray(window.dataLayer)) {
    objects.forEach(data => {
      const dataLayer = {
        pageType: getPageType(),
        device: getDevice(),
        ...data,
      };
      window.dataLayer.push(dataLayer);
    });
  }
}

export const useSettings = create<SettingsStore>((set, get) => ({
  isLoading: false,
  isAssignment: false,
  settings: undefined,
  fetchSettings: async (domain: string) => {
    if (isBrowser()) {
      const { settings, isLoading } = get();
      if (!settings && !isLoading) {
        set({ isLoading: true });
        const [settings, offset]: [Settings, Offset] = await Promise.all([
          getSettings({ domain }),
          getOffset({ domain }),
        ]);

        const isAssignment = settings?.env?.service_type_layout === 'assignment';
        set({ settings, isAssignment, isLoading: false, ...offset });
        pushGtmObjects(settings.gtm_datalayer_objects);
      }
    }
  },
}));

export const useSettingsStore = (domain: string) => {
  const { fetchSettings, ...settingsStore } = useSettings();

  React.useEffect(() => {
    fetchSettings(domain);
  }, []);

  return settingsStore;
};

type PapersStore = {
  papers: PaperType[];
  isFetchingPapers: boolean;
  fetchPapers: (domain: string) => void;
};

const usePapers = create<PapersStore>((set, get) => ({
  papers: [],
  isFetchingPapers: false,
  fetchPapers: async (domain: string) => {
    if (isBrowser()) {
      const { isFetchingPapers, papers } = get();
      if (!papers.length && !isFetchingPapers) {
        set({ isFetchingPapers: true });
        const papers: PaperType[] = await getPapers({ domain });
        set({ papers, isFetchingPapers: false });
      }
    }
  },
}));

export const usePapersStore = (domain: string) => {
  const { fetchPapers, papers } = usePapers();

  React.useEffect(() => {
    fetchPapers(domain);
  }, []);

  return papers;
};

type DisciplineDictionary = {
  discipline_sub_grouping: DisciplineSubGrouping[];
  discipline_sub: DisciplineSub[];
};

type DisciplinesStore = {
  isDisciplinesLoading: boolean;
  discipline_sub_grouping: DisciplineSubGrouping[] | null;
  discipline_sub: DisciplineSub[] | null;
  fetchDisciplines: (domain: string) => void;
};

const useDisciplines = create<DisciplinesStore>((set, get) => ({
  isDisciplinesLoading: false,
  discipline_sub_grouping: null,
  discipline_sub: null,
  fetchDisciplines: async (domain: string) => {
    if (isBrowser()) {
      const { isDisciplinesLoading, discipline_sub_grouping, discipline_sub } = get();
      if (!discipline_sub_grouping && !discipline_sub && !isDisciplinesLoading) {
        set({ isDisciplinesLoading: true });
        const disciplines: DisciplineDictionary = await getDisciplines({ domain });
        set({ ...disciplines, isDisciplinesLoading: false });
      }
    }
  },
}));

export const useDisciplinesStore = (domain: string) => {
  const { fetchDisciplines, ...disciplines } = useDisciplines();

  React.useEffect(() => {
    fetchDisciplines(domain);
  }, []);

  return disciplines;
};
